import axios from 'axios';
import pluralize from 'pluralize';

const dmURL = process.env.VUE_APP_DOCUMENT_MANAGER_URL;
const apiURL = `${dmURL}/api/v3`;

const DMRequest = async ({
  method,
  url,
  endpoint,
  params,
  data,
  token,
}) => {
  const actualUrl = url || `${apiURL}${endpoint}`;
  const searchParams = new URLSearchParams(params);
  const headers = {
    Authorization: `Bearer ${token}`,
    Accept: 'application/json',
  };

  const response = await axios({
    method,
    url: actualUrl,
    params: searchParams,
    data,
    headers,
  });
  return response;
};

const DocumentManager = {
  methods: {
    async $_DocumentManager_listContainers({ token, url, endpoint = '/binders' }) {
      const response = await DMRequest({
        method: 'get', url, endpoint, token,
      });
      const containers = response.data.data.map((container) => ({
        id: container.data.id,
        type: container.data.type,
        label: container.data.label,
        shelf: container.data.shelf,
      }));
      const { next } = response.data.links;
      return { containers, next };
    },
    async $_DocumentManager_listAll({ resource, methodArguments }) {
      const methodName = `$_DocumentManager_list${resource.charAt(0).toUpperCase() + resource.slice(1)}`;
      let response;
      response = await this[methodName](methodArguments);
      const items = response[resource];
      while (response.next) {
        // eslint-disable-next-line no-await-in-loop
        response = await this[methodName]({
          ...methodArguments,
          url: response.next,
        });
        items.push(...response[resource]);
      }
      return items;
    },
    async $_DocumentManager_listAllExpandedContainers({ token }) {
      const containers = await this.$_DocumentManager_listAll({ resource: 'containers', methodArguments: { token } });
      const expandedContainers = await Promise.all(containers.map(async (container) => {
        try {
          const shelf = (await DMRequest({
            method: 'get', url: container.shelf.links.self.href, token,
          })).data.data;
          const cabinet = (await DMRequest({
            method: 'get', url: shelf.cabinet.links.self.href, token,
          })).data.data;
          return {
            id: container.id,
            type: container.type,
            label: container.label,
            shelf: {
              ...shelf,
              cabinet,
            },
          };
        } catch (error) {
          if (error.response.status === 403) {
            return undefined;
          }
          throw error;
        }
      }));
      return expandedContainers.filter(Boolean);
    },
    async $_DocumentManager_listDocuments({ token, url, container }) {
      const endpoint = container ? `/${pluralize(container.type)}/${container.id}/documents` : undefined;
      const response = await DMRequest({
        method: 'get', url, endpoint, token,
      });
      const documents = response.data.data.map((document) => ({
        id: document.data.id,
        url: `${dmURL}/documents/${document.data.id}`,
        title: document.data.title,
        createdAt: document.data.createdAt,
      }));
      const { next } = response.data.links;
      return { documents, next };
    },
    async $_DocumentManager_listAllDocuments({ token, container }) {
      return this.$_DocumentManager_listAll({ resource: 'documents', methodArguments: { token, container } });
    },
    async $_DocumentManager_moveDocument({ token, document, container }) {
      const endpoint = `/documents/${document.id}`;
      const data = {
        data: {
          container: {
            data: {
              id: container.id,
              type: container.type,
            },
          },
        },
      };
      return DMRequest({
        method: 'patch', endpoint, token, data,
      });
    },
    async $_DocumentManager_retrieveDocumentInformation({ token, document }) {
      const endpoint = `/documents/${document.id}/information?group[]=tags`;
      const response = await DMRequest({ method: 'get', endpoint, token });
      const information = Object.fromEntries(
        Object.entries(response.data.data)
          .map((singleInformation) => ([
            singleInformation[0],
            singleInformation[1][0].data.value.data,
          ])),
      );
      return information;
    },
  },
};

export default DocumentManager;
